<template>
    <div :class="['flex flex-col', compact ? 'text-[12px]' : '']">
        <div class="flex justify-between items-center">
            <div class="flex items-center">
                <button
                    :class="[
                        'border-player-contrast border-[2px] rounded-[6px] font-bold text-player-contrast focus:outline-none focus:ring-2 focus:ring-player-accent',
                        compact ? 'h-[16px] px-1 text-[6px]' : 'w-[24px] h-[20px] text-[10px]',
                    ]"
                    @click="changePlaybackRate()"
                    :aria-label="playbackSpeedLabel"
                    aria-live="polite">
                    {{ currentRate }}x
                </button>
            </div>

            <div class="flex items-center">
                <!-- back 10s button -->
                <button
                    @click="emit('back-seconds', 10)"
                    class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent"
                    :class="compact ? 'mr-2' : 'mr-6'"
                    :title="__('Back 10 seconds')"
                    :aria-label="__('Back 10 seconds')">
                    <IconRewind :class="compact ? 'w-[16px] h-[16px]' : 'w-[32px] h-[32px]'" />
                </button>

                <!-- play button -->
                <button
                    v-if="!playing"
                    @click="emit('play')"
                    class="aspect-square hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent"
                    :title="__('Play')"
                    :aria-label="__('Play')">
                    <IconPlay :class="compact ? 'w-[28px] h-[28px]' : 'w-[57px] h-[57px]'" />
                </button>

                <!-- pause button -->
                <button
                    v-else
                    @click="emit('pause')"
                    class="aspect-square hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent"
                    :title="__('Pause')"
                    :aria-label="__('Pause')">
                    <IconPause :class="compact ? 'w-[28px] h-[28px]' : 'w-[57px] h-[57px]'" />
                </button>

                <!-- skip 10s button -->
                <button
                    @click="emit('skip-seconds', 10)"
                    class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent"
                    :class="compact ? 'ml-2' : 'ml-6'"
                    :title="__('Skip 10 seconds')"
                    :aria-label="__('Skip 10 seconds')">
                    <IconForward :class="compact ? 'w-[16px] h-[16px]' : 'w-[32px] h-[32px]'" />
                </button>
            </div>

            <div class="flex items-center">
                <!-- mute button -->
                <button
                    v-if="!muted"
                    @click="emit('mute')"
                    class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent"
                    :title="__('Mute')"
                    :aria-label="__('Mute')">
                    <IconMute :class="compact ? 'w-[12px] h-[12px]' : 'w-[24px] h-[24px]'" />
                </button>

                <!-- unmute button -->
                <button
                    v-else
                    @click="emit('unmute')"
                    class="hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-player-accent"
                    :title="__('Unmute')"
                    :aria-label="__('Unmute')">
                    <IconUnmute :class="compact ? 'w-[12px] h-[12px]' : 'w-[24px] h-[24px]'" />
                </button>

                <ShareButton
                    v-if="showShare"
                    :seek="seek"
                    :episode-hash-id="episodeHashId"
                    :compact="compact"
                />
            </div>
        </div>

        <div class="flex flex-row justify-center space-x-2 mt-1">
        </div>
    </div>
</template>

<script setup>
import __ from "../helpers";
import IconPlay from "./IconPlay.vue";
import IconPause from "./IconPause.vue";
import IconRewind from "./IconRewind.vue";
import IconForward from "./IconForward.vue";
import IconMute from "./IconMute.vue";
import IconUnmute from "./IconUnmute.vue";
import ShareButton from "./ShareButton.vue";
import { computed, ref } from "vue";

const props = defineProps({
    playing: Boolean,
    muted: Boolean,
    playbackRate: Number,
    compact: Boolean,
    showShare: Boolean,
    seek: {
        type: Number,
        default: 0,
    },
    episodeHashId: {
        type: String,
        default: null,
    },
})

const emit = defineEmits(['play', 'pause', 'back-seconds', 'skip-seconds', 'mute', 'unmute', 'playback-rate'])

const playbackRates = [1, 1.5, 2]
const currentRate = ref(props.playbackRate)

const playbackSpeedLabel = computed(() => {
    return `${__('Playback speed')}, ${currentRate.value}x`;
});

function changePlaybackRate() {
    currentRate.value = playbackRates[(playbackRates.indexOf(currentRate.value) + 1) % playbackRates.length]

    emit('playback-rate', currentRate.value)
}

</script>
